import React, {useMemo} from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {
  getProviderCircleLogo,
  getProviderIcon,
  getProviderName,
  getSvgProviderLogo
} from '@vio/core/src/offer/business/provider'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {ICON_HEIGHT, LOGO_HEIGHT} from './constants'

export type ProviderLogoVariant = 'logo' | 'icon' | 'circle'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Which provider's logo to display */
  providerCode: string
  /** The variant of the logo to display */
  variant?: ProviderLogoVariant
  /** The height of the image */
  height?: number
  loading?: 'eager' | 'lazy'
  /** The brand info to display*/
  brandInfo?: SearchOffer['metadata']['brandInfo'] | null
}

const Wrapper = styled.div<{variant: ProviderLogoVariant; height?: number}>`
  display: flex;
  align-items: center;

  img {
    display: block;
    margin-right: ${cssTheme.layout.spacing.s200};
    height: ${({variant, height}) =>
      height ? height : variant === 'logo' ? LOGO_HEIGHT : ICON_HEIGHT}px;

    &:only-child {
      margin-right: 0;
    }
  }
`

const addCacheBuster = (url: string) => `${url}?v=2`

export const ProviderLogo = ({
  providerCode,
  variant = 'logo',
  height,
  className,
  loading = 'lazy',
  brandInfo = null
}: Props) => {
  const providerName = getProviderName(providerCode)

  const logoUrl = useMemo(() => {
    switch (variant) {
      case 'icon':
        return getProviderIcon({providerCode})
      case 'circle':
        return getProviderCircleLogo({providerCode})
      default:
        return getSvgProviderLogo({providerCode})
    }
  }, [variant, providerCode])

  const getMetadataProviderUrl = ({
    iconURL,
    logoURL
  }: {
    iconURL: string
    logoURL: string
  }) => {
    switch (variant) {
      case 'icon':
        return addCacheBuster(iconURL)

      case 'circle':
        return addCacheBuster(iconURL.replace('--icon', '--circle'))

      default:
        return addCacheBuster(logoURL)
    }
  }

  if (brandInfo && brandInfo.logoURL && brandInfo.iconURL) {
    const {logoURL, iconURL} = brandInfo
    const url = getMetadataProviderUrl({iconURL, logoURL})
    return (
      <Wrapper variant={variant} height={height} className={className}>
        <img
          src={url}
          // -SAPI is used to indicate that the logo is from Offer's brandInfo - for easier debugging
          alt={`${providerCode}-logo-SAPI`}
          title={brandInfo.brandName}
          height={height}
          loading={loading}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper variant={variant} height={height} className={className}>
      <img
        src={logoUrl}
        alt={`${providerCode}-logo`}
        title={providerName}
        height={height}
        loading={loading}
      />
    </Wrapper>
  )
}
